import { Box, SxProps, Theme } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { IOT_ORIGIN, IOT_PROTOCOL, IOT_SRC } from '../../../commonUtils/config';

/**
 * Embeds the Geneo IoT block editor and talks to it over postMessage.
 *
 * The contract (v1) is:
 *
 * | Direction     | type                    | payload                                             |
 * | ------------- | ----------------------- | --------------------------------------------------- |
 * | frame -> host | `geneo:ready`           | `{ protocol, version }`, no data                     |
 * | host -> frame | `geneo:init`            | `{ protocol, project?, meta?, sampleProjects?, readOnly?, features }` |
 * | host -> frame | `geneo:load`            | `{ project }`                                        |
 * | host -> frame | `geneo:request-project` | `{ id }`                                      |
 * | frame -> host | `geneo:project`         | `{ id?, project }` <- the JSON output         |
 * | frame -> host | `geneo:dirty`           | `{ dirty }`                                   |
 * | frame -> host | `geneo:error`           | `{ code, message }`                           |
 *
 * The frame announces itself first because `iframe.onload` fires before React
 * inside the frame has mounted and attached its listener - an `init` sent on
 * load is simply dropped. `geneo:init` transfers a `MessagePort`, and every
 * message after the handshake goes over that port: it is point to point, so no
 * other script on this page (analytics, chat widgets, extensions - everything
 * that posts to `window`) can read or inject into it.
 */
/**
 * How the project identifies itself inside the editor. On a blank start the
 * host supplies this so the student is not looking at "geneo_iot_untitled";
 * once a project is saved it travels inside the project file itself.
 */
export interface GeneoIotProjectMeta {
  name: string;
  author: string;
  description: string;
}

export interface GeneoIotProjectFile {
  version: string;
  meta: GeneoIotProjectMeta;
  board?: 'esp32' | 'esp8266' | 'arduino_uno' | 'arduino_nano';
  /** The blocks, and the source generated from them. */
  workspace: { xml: string; generatedCode: string };
  editorMode?: 'blocks' | 'code';
  savedAt: string;
}

export interface GeneoIotError {
  code: string;
  message: string;
}

/**
 * The frame emits behaviour only: enum-valued props, never identity and never
 * content. Identity is attached on this side, at the ingest boundary, and
 * nowhere else.
 */
export interface GeneoIotTelemetryOptions {
  /** Absent or false means the frame emits nothing. There is no default-on path. */
  enabled: boolean;
  /** Flush cadence, ms. Frame default 5000, clamped to [2000, 30000]. */
  flushIntervalMs?: number;
  /** Max events per envelope. Frame default 25, clamped to [1, 100]. */
  maxBatchSize?: number;
}

/** One telemetry event, resolved to wall-clock time on this side. */
export interface GeneoIotTelemetryEvent {
  /** 'session_start' | 'flash_result' | ... — the frame's own name, unprefixed. */
  name: string;
  /** Resolved against the host anchor; the frame only knows elapsed ms. */
  occurredAt: string;
  props: Record<string, string | number | boolean>;
  /** New on every frame mount; not stable across reloads. */
  sessionId: string;
  /** Batch counter. Gaps mean a lost batch. */
  seq: number;
  appVersion: string;
}

export interface GeneoIotHandle {
  /** Ask the frame for the student's current work. Rejects if it stays silent. */
  requestProject: () => Promise<GeneoIotProjectFile>;
  /** Push a project into the editor, replacing whatever is open. */
  load: (project: GeneoIotProjectFile) => void;
  /** True once the handshake has completed and the port is open. */
  isReady: () => boolean;
}

interface IProps {
  /** Opened in the editor as soon as the frame announces itself. */
  initialProject?: GeneoIotProjectFile;
  /**
   * Names the work the student is about to start. Sent on `geneo:init`; the
   * frame uses it for the blank project it creates when `initialProject` is
   * absent.
   */
  meta?: GeneoIotProjectMeta;
  /**
   * Reference solutions, offered inside the frame as read-only "Samples".
   * The menu lists them in order and names each entry from its own `meta.name`,
   * so they need distinct names. The frame keeps at most 12 and drops any entry
   * that has no `workspace.xml`.
   */
  sampleProjects?: GeneoIotProjectFile[];
  readOnly?: boolean;
  title?: string;
  sx?: SxProps<Theme>;
  /** A project the frame pushed on its own, i.e. not answering a request. */
  onProject?: (project: GeneoIotProjectFile) => void;
  /** Unsaved-work signal - drive a confirmation prompt off this. */
  onDirty?: (dirty: boolean) => void;
  onError?: (error: GeneoIotError) => void;
  onReady?: () => void;
  /**
   * Opt in to usage telemetry. Off unless set: the frame emits nothing unless
   * the host asks, which is an invariant of the telemetry contract.
   */
  telemetry?: GeneoIotTelemetryOptions;
  /**
   * A decoded, de-duplicated, time-resolved batch of telemetry events.
   *
   * Called on the UI path, so it must not block: queue and return. Delivery
   * failures are the caller's problem to swallow — telemetry must never
   * degrade the editor.
   */
  onTelemetry?: (events: GeneoIotTelemetryEvent[]) => void;
}

/** How long `requestProject()` waits before giving up on the frame. */
const REQUEST_PROJECT_TIMEOUT_MS = 5000;

const styles: { frame: SxProps<Theme> } = {
  frame: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },
};

/**
 * Messages are untrusted input even after the origin check passes, so the shape
 * is verified before anything is handed to a caller.
 */
const isProjectFile = (value: unknown): value is GeneoIotProjectFile => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const workspace = (value as GeneoIotProjectFile).workspace;
  return typeof workspace === 'object' && typeof workspace?.xml === 'string';
};

/** `crypto.randomUUID` needs a secure context, which a LAN dev host is not. */
const createRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface PendingRequest {
  resolve: (project: GeneoIotProjectFile) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export const GeneoIotFrame = forwardRef<GeneoIotHandle, IProps>(
  function GeneoIotFrame(props, ref) {
    const { title, sx } = props;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const portRef = useRef<MessagePort | null>(null);
    const pendingRef = useRef(new Map<string, PendingRequest>());
    // StrictMode mounts twice and the cleanup reloads the frame, so an
    // unguarded call would announce two opens in development.
    const readyFiredRef = useRef(false);
    /**
     * Wall-clock instant the frame announced itself.
     *
     * Telemetry carries elapsed milliseconds, not dates, because school lab
     * machines routinely have wrong system clocks — absolute timestamps from
     * inside the frame would place a measurable share of events in the wrong
     * year. Everything is resolved against this anchor instead.
     */
    const sessionAnchorRef = useRef(0);
    /**
     * (sessionId, seq) already ingested. A host-requested flush racing a
     * pagehide flush delivers the same batch twice, and our own event ids are
     * minted per call — so without this the duplicate would be stored, not
     * deduplicated.
     */
    const seenBatchesRef = useRef(new Set<string>());
    // The handshake effect runs once, so it reads props through a ref instead
    // of closing over the values the component first rendered with.
    const propsRef = useRef(props);
    propsRef.current = props;

    useEffect(() => {
      const pending = pendingRef.current;

      const failPending = (reason: string) => {
        pending.forEach((request) => {
          clearTimeout(request.timer);
          request.reject(new Error(reason));
        });
        pending.clear();
      };

      const handlePortMessage = (data: unknown) => {
        if (typeof data !== 'object' || data === null) {
          return;
        }
        const message = data as Record<string, unknown>;
        switch (message.type) {
          case 'geneo:project': {
            const { id, project } = message;
            if (!isProjectFile(project)) {
              propsRef.current.onError?.({
                code: 'invalid-project',
                message: 'Geneo IoT sent a project in an unexpected shape',
              });
              return;
            }
            if (typeof id === 'string') {
              const request = pending.get(id);
              if (request) {
                clearTimeout(request.timer);
                pending.delete(id);
                request.resolve(project);
              }
              return;
            }
            propsRef.current.onProject?.(project);
            return;
          }
          case 'geneo:telemetry': {
            handleTelemetry(message);
            return;
          }
          case 'geneo:dirty':
            if (typeof message.dirty === 'boolean') {
              propsRef.current.onDirty?.(message.dirty);
            }
            return;
          case 'geneo:error':
            propsRef.current.onError?.({
              code: String(message.code ?? 'unknown'),
              message: String(message.message ?? ''),
            });
            return;
          default:
            return;
        }
      };

      /**
       * Decode one telemetry envelope: validate, drop repeats, resolve times,
       * hand it on. Anything malformed is ignored rather than partially
       * ingested — a half-read batch is worse than a missing one.
       */
      const handleTelemetry = (message: Record<string, unknown>) => {
        if (message.protocol !== IOT_PROTOCOL) return;
        if (!Array.isArray(message.events)) return;

        const sessionId = String(message.sessionId ?? '');
        const seq = Number(message.seq);
        if (!sessionId || !Number.isFinite(seq)) return;

        const key = `${sessionId}:${seq}`;
        if (seenBatchesRef.current.has(key)) return;
        seenBatchesRef.current.add(key);

        const anchor = sessionAnchorRef.current || Date.now();
        const appVersion = String(message.appVersion ?? '');

        const events: GeneoIotTelemetryEvent[] = [];
        for (const raw of message.events as unknown[]) {
          if (typeof raw !== 'object' || raw === null) continue;
          const e = raw as Record<string, unknown>;
          if (typeof e.name !== 'string' || !e.name) continue;

          const t = Number(e.t);
          if (!Number.isFinite(t)) continue;

          events.push({
            name: e.name,
            occurredAt: new Date(anchor + t).toISOString(),
            props:
              typeof e.props === 'object' && e.props !== null
                ? (e.props as Record<string, string | number | boolean>)
                : {},
            sessionId,
            seq,
            appVersion,
          });
        }

        if (events.length > 0) {
          propsRef.current.onTelemetry?.(events);
        }
      };

      const handleReady = (event: MessageEvent) => {
        // Exact origin equality - `startsWith` would accept
        // https://iot.geneo.com.evil.io - and the right window, because any
        // page on the internet can post to ours.
        if (event.origin !== IOT_ORIGIN) {
          return;
        }
        if (
          !event.source ||
          event.source !== iframeRef.current?.contentWindow
        ) {
          return;
        }
        const data = event.data as Record<string, unknown> | null;
        if (typeof data !== 'object' || data === null) {
          return;
        }
        if (data.type !== 'geneo:ready') {
          return;
        }
        if (data.protocol !== IOT_PROTOCOL) {
          propsRef.current.onError?.({
            code: 'protocol',
            message: `Unsupported Geneo IoT protocol ${String(data.protocol)}`,
          });
          return;
        }

        // Anchored the moment the frame announces itself, before any event
        // can arrive, so every `t` in this session resolves against it.
        sessionAnchorRef.current = Date.now();

        // A second `ready` means the frame reloaded; the old port is dead and
        // anything waiting on it will never be answered.
        if (portRef.current) {
          portRef.current.close();
          failPending('Geneo IoT reloaded');
        }

        const channel = new MessageChannel();
        portRef.current = channel.port1;
        channel.port1.onmessage = (portEvent) =>
          handlePortMessage(portEvent.data);
        channel.port1.start();


        const { initialProject, meta, sampleProjects, readOnly, telemetry } =
        propsRef.current;
        (event.source as Window).postMessage(
          {
            type: 'geneo:init',
            protocol: IOT_PROTOCOL,
            // Left off the message entirely rather than sent as `undefined`:
            // an absent `project` is how the frame is told to start blank, and
            // it then names that blank project from `meta`.
            ...(initialProject ? { project: initialProject } : {}),
            ...(meta ? { meta } : {}),
            ...(sampleProjects?.length ? { sampleProjects } : {}),
            readOnly: readOnly,
            // Omitted entirely when the host has not opted in, so a frame
            // build with telemetry stays inert rather than defaulting on.
            ...(telemetry?.enabled ? { telemetry } : {}),
            features: {
              // Web Serial is desktop Chromium only; the frame degrades to
              // simulation-only when this is false.
              flash: typeof navigator !== 'undefined' && 'serial' in navigator,
              compile: true,
            },
          },
          // Never '*': if the frame is ever redirected the browser would hand
          // the payload to whatever origin now sits in that window.
          IOT_ORIGIN,
          [channel.port2]
        );

        // After the port is open and init is posted: the editor is genuinely
        // usable at this point, which is what makes this worth reporting.
        if (!readyFiredRef.current) {
          readyFiredRef.current = true;
          propsRef.current.onReady?.();
        }
      };

      window.addEventListener('message', handleReady);
      // The `src` is assigned here rather than in JSX so the listener above is
      // guaranteed to exist before the frame can load and announce itself.
      // Re-assigning it also reloads the frame, which is what makes StrictMode's
      // double mount work: the second pass gets a fresh `ready` after the first
      // pass's cleanup closed the port.
      if (iframeRef.current) {
        iframeRef.current.src = IOT_SRC;
      }

      return () => {
        // Ask for the final flush BEFORE the port goes away: a destroyed frame
        // takes its unsent buffer with it, so session_end and everything still
        // queued would be lost on every unmount.
        portRef.current?.postMessage({ type: 'geneo:flush-telemetry' });
        window.removeEventListener('message', handleReady);
        portRef.current?.close();
        portRef.current = null;
        failPending('Geneo IoT frame was closed');
      };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        requestProject: () => {
          const port = portRef.current;
          if (!port) {
            return Promise.reject(new Error('Geneo IoT is not ready yet'));
          }
          const id = createRequestId();
          return new Promise<GeneoIotProjectFile>((resolve, reject) => {
            const timer = setTimeout(() => {
              pendingRef.current.delete(id);
              reject(new Error('Geneo IoT did not respond'));
            }, REQUEST_PROJECT_TIMEOUT_MS);
            pendingRef.current.set(id, { resolve, reject, timer });
            port.postMessage({ type: 'geneo:request-project', id });
          });
        },
        load: (project: GeneoIotProjectFile) => {
          portRef.current?.postMessage({ type: 'geneo:load', project });
        },
        isReady: () => portRef.current !== null,
      }),
      []
    );

    return (
      <Box
        component="iframe"
        ref={iframeRef}
        // `serial`/`bluetooth`/`usb` default to an allowlist of `self`, so a
        // cross-origin frame gets them only if we delegate explicitly. A bare
        // feature name means `feature 'src'`, i.e. granted to the frame's own
        // origin. Without this, requestPort() inside the frame throws
        // SecurityError and in-browser flashing is dead.
        allow="serial; bluetooth; usb"
        allowFullScreen
        title={title || 'Geneo IoT block editor'}
        sx={[styles.frame, ...(Array.isArray(sx) ? sx : [sx])]}
      />
    );
  }
);

export {
  getTeacherSolution,
  practicalProjectToProjectFile,
  projectFileToPracticalProject,
} from './practicalProject';

export default GeneoIotFrame;
